import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Search,
  Sparkles,
  ArrowRight,
  X,
  Book,
  Home,
  SlidersHorizontal,
  Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { t } from "@/i18n";

const DIALECT_LABEL = {
  MSA: "Arabe standard",
  "darija-maroc": "Darija marocaine",
  "darija-algerie": "Darija algérienne",
  egyptien: "Égyptien",
  levantin: "Levantin",
};

function dialectLabel(d) {
  return DIALECT_LABEL[d] || d;
}

function categoryLabel(c) {
  return (c || "general")
    .replace(/[-_]/g, " ")
    .replace(/^\w/, (m) => m.toUpperCase());
}

/* Menu public réutilisé sur toutes les pages hors connexion : toujours un
   moyen visible de revenir à l'accueil ou au dictionnaire, jamais seulement
   le logo. */
function PublicNav() {
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 pt-4 pb-3 bg-[#070d14]/90 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="max-w-6xl mx-auto px-4 flex items-center gap-2">
        <Link to="/fr" className="flex items-center gap-2.5 shrink-0 mr-2">
          <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center font-head font-bold arabic">
            ع
          </div>
          <span className="hidden sm:block font-head font-extrabold text-lg">
            {t("navbar.harf")}
          </span>
        </Link>
        <nav className="flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-1.5 py-1.5">
          <Link
            to="/fr"
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Home size={14} /> <span className="hidden sm:inline">{t("coursequestions.accueil")}</span>
          </Link>
          <Link
            to="/fr/dictionary"
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white bg-white/10 transition"
          >
            <Book size={14} /> {t("navbar.dictionnaire")}
          </Link>
        </nav>
        <div className="flex-1" />
        <button
          onClick={() => nav("/fr/auth")}
          className="px-5 py-2.5 rounded-full bg-white/12 border border-white/20 backdrop-blur-xl text-sm font-semibold hover:bg-white/20 transition shrink-0"
        >
          {t("dictionary.commencer")}
        </button>
      </div>
    </header>
  );
}

export default function Dictionary() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [dialect, setDialect] = useState("all");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState(null); // null = pas encore chargé
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);
  const PAGE_SIZE = 40;

  useEffect(() => {
    document.title = "Dictionnaire arabe gratuit, standard et dialectal | Harf";
    const setMeta = (attr, key, content) => {
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta(
      "name",
      "description",
      "Cherchez un mot en arabe littéral, marocain, algérien, égyptien ou levantin. Traduction, prononciation et audio pour chaque mot, filtrable par thème et par langue.",
    );
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.origin + "/fr/dictionary");

    const setAlternate = (id, hreflang, href) => {
      let tag = document.head.querySelector(`link[data-hreflang-id="${id}"]`);
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "alternate");
        tag.setAttribute("data-hreflang-id", id);
        document.head.appendChild(tag);
      }
      tag.setAttribute("hreflang", hreflang);
      tag.setAttribute("href", href);
    };
    setAlternate("fr", "fr", window.location.origin + "/fr/dictionary");
    setAlternate("en", "en", window.location.origin + "/dictionary");
  }, []);

  useEffect(() => {
    api
      .get("/dict/categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  // Chaque changement de filtre ou de recherche revient à la page 1 — sinon
  // on peut se retrouver sur une page 4 qui n'existe plus pour le nouveau
  // filtre, avec une liste vide et aucune explication.
  useEffect(() => {
    setPage(1);
  }, [query, dialect, category]);

  useEffect(() => {
    setItems(null);
    setError(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const p = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (query.trim()) p.set("q", query.trim());
      if (dialect !== "all") p.set("dialect", dialect);
      if (category !== "all") p.set("category", category);
      api
        .get(`/dict/concepts?${p.toString()}`)
        .then((r) => {
          setItems(r.data.items || []);
          setTotal(r.data.total || 0);
        })
        .catch(() => {
          setItems([]);
          setError(true);
        });
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, dialect, category, page]);

  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Connecté : on suit le thème de l'app (jour/nuit). Visiteur : la DA
  // sombre de la landing, qui est la vitrine du site.
  const shell = user
    ? "min-h-screen bg-background text-foreground"
    : "min-h-screen bg-[#070d14] text-white";
  const muted = user ? "text-muted-foreground" : "text-white/55";
  const chipOff = user
    ? "border bg-card text-muted-foreground hover:text-foreground hover:bg-muted"
    : "border border-white/12 bg-white/[0.05] text-white/65 hover:text-white hover:bg-white/10";
  const chipOn = user
    ? "bg-primary text-primary-foreground"
    : "bg-white text-[#070d14]";
  const card = user
    ? "border bg-card hover:bg-muted"
    : "border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/15";

  return (
    <div className={shell}>
      {/* Connecté : la vraie navbar de l'app vient de Shell (MaybeShell
          dans App.jsx). On n'affiche celle-ci que pour les visiteurs non
          connectés, pour ne jamais donner l'impression de déconnexion. */}
      {!user && <PublicNav />}

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.07] px-4 py-1.5 mb-6">
            <Sparkles size={13} className="text-amber-300" />
            <span className="text-sm text-white/80">
              {t("dictionary.gratuit_sans_compte_5_varietes_d")}
            </span>
          </div>
          <h1 className="font-head text-3xl sm:text-5xl font-light mb-4">
            {t("dictionary.le_dictionnaire")} <span className="font-bold">arabe</span>
          </h1>
          <p className={`${muted} max-w-xl mx-auto mb-8`}>
            {t("dictionary.un_mot_toutes_ses_langues_rassemblees")}
          </p>

          <div className="relative max-w-xl mx-auto">
            <Search
              size={18}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-white/35"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("dictionary.chercher_un_mot_en_arabe_ou")}
              className="w-full pl-12 pr-11 py-4 rounded-full border border-white/12 bg-white/[0.06] backdrop-blur-xl outline-none focus:border-white/25 placeholder:text-white/35 text-[15px]"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                aria-label={t("dictionary.effacer")}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* ==================== FILTRES ==================== */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-white/40">
            <SlidersHorizontal size={14} />
            <span className="text-xs font-semibold uppercase tracking-wide">
              {t("dictionary.langue")}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {[
              { id: "all", label: "Toutes les langues" },
              ...Object.entries(DIALECT_LABEL).map(([id, label]) => ({
                id,
                label,
              })),
            ].map((d) => (
              <button
                key={d.id}
                onClick={() => setDialect(d.id)}
                className={`px-3.5 py-2 rounded-full text-sm font-medium transition ${
                  dialect === d.id ? chipOn : chipOff
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>

          {categories.length > 0 && (
            <>
              <div className="flex items-center gap-2 mb-3 text-white/40">
                <SlidersHorizontal size={14} />
                <span className="text-xs font-semibold uppercase tracking-wide">
                  {t("dictionary.theme")}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCategory("all")}
                  className={`px-3.5 py-2 rounded-full text-sm font-medium transition ${
                    category === "all" ? chipOn : chipOff
                  }`}
                >
                  {t("dictionary.tous_les_themes")}
                </button>
                {categories.map((c) => (
                  <button
                    key={c.category}
                    onClick={() => setCategory(c.category)}
                    className={`px-3.5 py-2 rounded-full text-sm font-medium transition ${
                      category === c.category ? chipOn : chipOff
                    }`}
                  >
                    {c.category
                      .replace(/[-_]/g, " ")
                      .replace(/^\w/, (m) => m.toUpperCase())}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ==================== RÉSULTATS ==================== */}
        {items === null && (
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm py-16">
            <Loader2 size={16} className="animate-spin" /> {t("lessoneditor.chargement")}
          </div>
        )}

        {items !== null && error && (
          <div className="rounded-3xl border border-red-500/25 bg-red-500/[0.06] p-6 text-center text-sm text-white/60 max-w-md mx-auto">
            {t("dictionary.le_dictionnaire_n_a_pas_pu")}{" "}
            <code className="px-1.5 py-0.5 rounded bg-white/10">
              /api/dict/_health
            </code>
            .
          </div>
        )}

        {items !== null && !error && items.length === 0 && (
          <div className="rounded-3xl border border-amber-500/25 bg-amber-500/[0.06] p-6 text-center text-sm text-white/60 max-w-md mx-auto">
            {t("dictionary.aucun_mot_ne_correspond_a_cette")}
          </div>
        )}

        {items !== null && !error && items.length > 0 && (
          <>
            <p className="text-xs text-white/35 mb-3">
              {total} mot{total > 1 ? "s" : ""}
            </p>
            <div className="space-y-2">
              {items.map((it, idx) => {
                const primary =
                  it.forms.find((f) => f.dialect === "MSA") || it.forms[0];

                // FIX: Dédoublonnage des mots arabes identiques
                const uniqueForms = [];
                it.forms.forEach((f) => {
                  const existing = uniqueForms.find(
                    (uf) => uf.word_ar === f.word_ar,
                  );
                  if (existing) {
                    // Si le mot existe déjà, on ajoute juste le dialecte à la liste pour l'infobulle
                    if (!existing.dialects.includes(f.dialect)) {
                      existing.dialects.push(f.dialect);
                    }
                  } else {
                    // Sinon on l'ajoute avec son dialecte initial
                    uniqueForms.push({ ...f, dialects: [f.dialect] });
                  }
                });

                return (
                  <Link
                    key={`${it.concept}-${idx}`}
                    to={`/fr/dictionary/${primary.dialect}/${primary.slug}`}
                    className={`flex items-center justify-between gap-4 rounded-2xl px-5 py-3.5 transition ${card}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold truncate">
                        {it.concept}
                      </div>
                      <div className="text-[11px] text-white/30 mt-0.5">
                        {it.category
                          .replace(/[-_]/g, " ")
                          .replace(/^\w/, (m) => m.toUpperCase())}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end max-w-[55%]">
                      {uniqueForms.map((f) => (
                        <span
                          key={`${f.dialects[0]}-${f.slug || f.word_ar}`}
                          title={f.dialects.map(dialectLabel).join(", ")}
                          className="arabic text-base px-2 py-1 rounded-lg bg-white/[0.05] border border-white/[0.06]"
                          dir="rtl"
                        >
                          {f.word_ar}
                        </span>
                      ))}
                    </div>
                  </Link>
                );
              })}
            </div>

            {maxPage > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-full border border-white/12 bg-white/[0.05] text-sm disabled:opacity-30 hover:bg-white/10 transition"
                >
                  {t("tutorial.precedent")}
                </button>
                <span className="text-xs text-white/40">
                  {t("dictionary.page")} {page} / {maxPage}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                  disabled={page >= maxPage}
                  className="px-4 py-2 rounded-full border border-white/12 bg-white/[0.05] text-sm disabled:opacity-30 hover:bg-white/10 transition"
                >
                  {t("dictionary.suivant")}
                </button>
              </div>
            )}
          </>
        )}

        {/* Le bloc d'inscription n'a aucun sens pour quelqu'un de déjà
            connecté : on ne le montre qu'aux visiteurs. */}
        {!user && (
          <div className="mt-16 rounded-[2rem] border border-white/[0.1] bg-white/[0.02] p-10 text-center">
            <Book size={28} className="mx-auto text-white/40 mb-4" />
            <h2 className="font-head text-2xl font-bold mb-3">
              {t("dictionary.ce_dictionnaire_n_est_qu_un")}
            </h2>
            <p className="text-white/55 max-w-lg mx-auto mb-7">
              {t("dictionary.apprends_l_arabe_litteral_pour_de")}
            </p>
            <button
              onClick={() => nav("/fr/auth")}
              className="px-7 py-3.5 rounded-full bg-white text-[#070d14] font-bold hover:bg-white/90 transition inline-flex items-center gap-2"
            >
              {t("dictionary.essayer_gratuitement_pendant_1_semaine")} <ArrowRight size={17} />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
